"use client";

import { useEffect, useMemo, useState } from "react";
import { Button, InputField } from "@vionixasia/shared-components";
import { AdminShell } from "@/components/AdminShell";
import { ResultTable } from "@/components/ResultTable";
import { apiClient } from "@/lib/api-client";
import { useQueryParam } from "@/lib/use-query-param";
import type { RaceResult } from "@/types/result";

export default function ResultsPage() {
  const projectId = useQueryParam("projectId");
  const eventId = useQueryParam("eventId");
  const [results, setResults] = useState<RaceResult[]>([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState("");

  const basePath = `/api/projects/${projectId}/events/${eventId}`;

  async function load() {
    if (!projectId || !eventId) return;
    const query = new URLSearchParams();
    if (search) query.set("search", search);
    if (category) query.set("category", category);
    if (status) query.set("whatsapp_status", status);
    const data = await apiClient<{ results: RaceResult[] }>(`${basePath}/results?${query.toString()}`);
    setResults(data.results);
  }

  useEffect(() => {
    load();
  }, [projectId, eventId]);

  const categories = useMemo(() => Array.from(new Set(results.map((result) => result.category).filter(Boolean))) as string[], [results]);

  async function send(ids: string[]) {
    setProgress(`Sending 0 / ${ids.length} messages`);
    const data = await apiClient<{ processed: number; success: number; failed: number }>(`${basePath}/whatsapp/send`, {
      method: "POST",
      body: JSON.stringify({ result_ids: ids }),
    });
    setProgress(`Sending ${data.processed} / ${ids.length} messages. Success: ${data.success}. Failed: ${data.failed}.`);
    await load();
  }

  return (
    <AdminShell>
      <h1 className="text-2xl font-semibold text-ink">Results</h1>
      <div className="mt-5 flex flex-wrap items-end gap-3 rounded-card border border-line bg-white p-4">
        <InputField wrapperClassName="min-w-[240px] flex-1" label="Search" name="search" placeholder="Nama / BIB / phone" value={search} onChange={(e) => setSearch(e.target.value)} labelColor="text-ink" />
        <InputField wrapperClassName="min-w-[200px]" label="Category" name="category" type="select" value={category} onChange={(e) => setCategory(e.target.value)} labelColor="text-ink" defaultOptionLabel={null}>
          <option value="">All categories</option>
          {categories.map((item) => <option key={item} value={item}>{item}</option>)}
        </InputField>
        <InputField wrapperClassName="min-w-[220px]" label="WhatsApp Status" name="status" type="select" value={status} onChange={(e) => setStatus(e.target.value)} labelColor="text-ink" defaultOptionLabel={null}>
          <option value="">All WhatsApp status</option>
          {["not_sent", "queued", "sent", "delivered", "read", "failed", "skipped"].map((item) => <option key={item} value={item}>{item}</option>)}
        </InputField>
        <Button color="teal" onClick={load}>Apply</Button>
      </div>
      {progress && <p className="mt-4 rounded bg-blue-50 p-3 text-sm text-blue-700">{progress}</p>}
      <div className="mt-5"><ResultTable results={results} onSend={send} /></div>
    </AdminShell>
  );
}
